import { nullishFilter, arrayToObj, objectMap, byIdArrayToObj } from '@caresend/utils';
import update from 'immutability-helper';
import { isProd } from '../../database/firebaseConfig/init.js';
import { setOffices } from './office.js';
import { setStaffUsers, setUsers } from './users.js';

/**
 * Takes an `ItineraryData` array and sets itineraries, waypoints, waypointActions, orders,
 * bookings and procedures in the
 * appropriate store modules.
 *
 * Required mutations:
 * - `'waypoint/SET_ITINERARIES' - ByID<Itinerary>`
 * - `'bookings/SET_BOOKINGS' - ByID<Booking>`
 * - `'orders/SET_ORDERS' - ByID<Order>`
 * - `'procedures/SET_PROCEDURES' - ByID<WithFriendlyStatus<Procedure>>`
 * - `'waypoint/SET_WAYPOINTS' - ByID<EncryptedWaypoint>`
 * - `'places/SET_PLACES' - ByID<Place>`
 * - `'places/SET_PLACES' - ByID<Place>`
 * - `'waypoint/SET_SAMPLE_PROCESSING_ACTIONS' - ByID<SampleProcessingAction>`
 * - `'waypoint/SET_WAYPOINT_ACTIONS' - ByID<WaypointAction>`
 *
 * Must pass `store` argument to enable dev logging and optional mutations.
 * In actions, you may pass `this`, if you use the following syntax:
 * ```
 *  async 'orderFlow/fetchBookingForViewing'(
 *    this: CustomRootStore,
 *    { commit, getters },
 *    bookingID,
 *  ) {
 * ```
 */
const setItineraryDataInStore = (commit, itineraryData, 
/** Pass store to enable dev logging and optional mutations */
store) => {
    /** Itineraries */
    const itineraryDataByIdArray = Object.values(itineraryData)
        .filter(nullishFilter);
    const itinerariesWithData = arrayToObj(itineraryDataByIdArray, 'id');
    const itineraries = objectMap(itinerariesWithData, (waypoint) => update(waypoint, { $unset: [
            'waypointsData',
            'nurseData',
        ] }));
    /** Waypoints */
    const waypointDataByIdArray = Object.values(itinerariesWithData)
        .map((itinerary) => itinerary.waypointsData)
        .filter(nullishFilter);
    const waypointsWithData = byIdArrayToObj(waypointDataByIdArray);
    const waypoints = objectMap(waypointsWithData, (waypoint) => update(waypoint, { $unset: [
            'waypointActionsData',
            'itineraryData',
            'placeData',
        ] }));
    /** Processing Actions */
    const sampleProcessingActionsData = Object.values(waypointsWithData)
        .map((waypoint) => waypoint.sampleProcessingActionsData)
        .filter(nullishFilter);
    const sampleProcessingActions = byIdArrayToObj(sampleProcessingActionsData);
    /** Places */
    const placeDataArray = Object.values(waypointsWithData)
        .map((waypoint) => waypoint.placeData)
        .filter(nullishFilter);
    const places = arrayToObj(placeDataArray, 'id');
    const placeGroupDataArray = Object.values(waypointsWithData)
        .map((waypoint) => waypoint.placeGroupData)
        .filter(nullishFilter);
    const placeGroups = arrayToObj(placeGroupDataArray, 'id');
    /** WaypointActions */
    const waypointActionDataByIdArray = Object.values(waypointsWithData)
        .map((waypoint) => waypoint.waypointActionsData)
        .filter(nullishFilter);
    const waypointActionsWithData = byIdArrayToObj(waypointActionDataByIdArray);
    const waypointActions = objectMap(waypointActionsWithData, (waypointAction) => update(waypointAction, { $unset: [
            'waypointData',
            'proceduresData',
        ] }));
    /** Procedures */
    const procedureDataByIdArray = Object.values(waypointActionsWithData)
        .map((waypointAction) => waypointAction.proceduresData)
        .filter(nullishFilter);
    const proceduresWithData = byIdArrayToObj(procedureDataByIdArray);
    const procedures = objectMap(proceduresWithData, (procedure) => update(procedure, { $unset: [
            'bookingData',
            'nurseData',
            'waypointActionsData',
        ] }));
    /** Shipments */
    const shipmentDataByIdArray = Object.values(waypointActionsWithData)
        .map((waypointAction) => waypointAction.shipmentsData)
        .filter(nullishFilter);
    const shipments = byIdArrayToObj(shipmentDataByIdArray);
    /** Bookings */
    const bookingDataArray = Object.values(proceduresWithData)
        .map((procedure) => procedure.bookingData)
        .filter(nullishFilter);
    const bookingsWithData = arrayToObj(bookingDataArray, 'id');
    const bookings = objectMap(bookingsWithData, (booking) => update(booking, { $unset: [
            'orderData',
            'patientData',
            'proceduresData',
        ] }));
    /** Orders */
    const orderDataArray = Object.values(bookingsWithData)
        .map((booking) => booking.orderData)
        .filter(nullishFilter);
    const ordersWithData = arrayToObj(orderDataArray, 'id');
    const orders = objectMap(ordersWithData, (order) => update(order, { $unset: [
            'bookingsData',
            'officeData',
            'prescriberData',
        ] }));
    /** Set Required */
    commit('waypoint/SET_ITINERARIES', itineraries);
    commit('waypoint/SET_WAYPOINT_ACTIONS', waypointActions);
    commit('waypoint/SET_WAYPOINTS', waypoints);
    commit('waypoint/SET_SAMPLE_PROCESSING_ACTIONS', sampleProcessingActions);
    commit('places/SET_PLACES', places);
    commit('places/SET_PLACE_GROUPS', placeGroups);
    commit('orders/SET_ORDERS', orders);
    commit('bookings/SET_BOOKINGS', bookings);
    commit('procedures/SET_PROCEDURES', procedures);
    commit('supplies/SET_SHIPMENTS', shipments);
    /** StaffUsers */
    const { staffUsersSet, staffUsers } = setStaffUsers(commit, proceduresWithData, orderDataArray, store);
    /** Users */
    const { usersSet, users } = setUsers(commit, bookingsWithData, store);
    /** Offices */
    const { officesSet, offices } = setOffices(commit, orderDataArray, store);
    /** Logs (dev mode only) */
    if (!isProd && !process.env.VUE_APP_SUPPRESS_LOGS) {
        console.info('setItineraryDataInStore has set:');
        console.table({
            orders: orderDataArray.length,
            bookings: bookingDataArray.length,
            procedures: procedureDataByIdArray.length,
            offices: officesSet,
            shipments: Object.keys(shipments).length,
            staffUsers: staffUsersSet,
            users: usersSet,
            waypoints: Object.keys(waypoints).length,
            waypointActions: Object.keys(waypointActions).length,
            itineraries: Object.keys(itineraries).length,
            places: Object.keys(places).length,
            placeGroups: Object.keys(placeGroups).length,
        });
    }
    return {
        bookings,
        orders,
        offices,
        procedures,
        shipments,
        staffUsers,
        users,
        waypointActions,
        waypoints,
        itineraries,
        places,
        placeGroups,
    };
};

export { setItineraryDataInStore };
